import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common/FormField";
import { cnInput } from "@/components/common/formClasses";
import { CoordinatePicker } from "./CoordinatePicker";
import {
  useCustomAddressDetail,
  useDeleteCustomAddress,
  useUpsertCustomAddress,
} from "../hooks/useCustomAddresses";
import { useCustomAddressStore } from "../stores/customAddressStore";
import type { CustomAddressDto, ReqUpsertCustomAddress } from "../types/customAddress.types";

// 서버(CustomAddressService)와 같은 대한민국 범위. 위경도 뒤바뀜을 프론트에서 먼저 잡는다.
const schema = z.object({
  addr01: z.string().trim().min(1, "시/도는 필수입니다").max(8, "8자 이내"),
  addr02: z.string().trim().max(32, "32자 이내"),
  addr03: z.string().trim().max(64, "64자 이내"),
  addr04: z.string().trim().max(32, "32자 이내"),
  roadName: z.string().trim().max(64, "64자 이내"),
  buildingNo: z.string().trim().max(16, "16자 이내"),
  placeName: z.string().trim().max(64, "64자 이내"),
  latitude: z.coerce
    .number({ invalid_type_error: "숫자를 입력하세요" })
    .min(33, "위도는 33~39.5 범위")
    .max(39.5, "위도는 33~39.5 범위"),
  longitude: z.coerce
    .number({ invalid_type_error: "숫자를 입력하세요" })
    .min(124, "경도는 124~132.5 범위")
    .max(132.5, "경도는 124~132.5 범위"),
  keywordsText: z.string(),
  memo: z.string().trim().max(256, "256자 이내"),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_VALUES: FormValues = {
  addr01: "",
  addr02: "",
  addr03: "",
  addr04: "",
  roadName: "",
  buildingNo: "",
  placeName: "",
  latitude: 0,
  longitude: 0,
  keywordsText: "",
  memo: "",
};

const toFormValues = (dto: CustomAddressDto): FormValues => ({
  addr01: dto.addr01,
  addr02: dto.addr02,
  addr03: dto.addr03,
  addr04: dto.addr04,
  roadName: dto.roadName,
  buildingNo: dto.buildingNo,
  placeName: dto.placeName,
  latitude: dto.latitude,
  longitude: dto.longitude,
  keywordsText: dto.keywords.join(", "),
  memo: dto.memo ?? "",
});

const splitKeywords = (text: string): string[] =>
  text
    .split(/[,\n]/)
    .map((keyword) => keyword.trim())
    .filter((keyword) => keyword.length > 0);

interface CustomAddressFormProps {
  mode: "add" | "edit";
  caIdx: number | null;
}

export function CustomAddressForm({ mode, caIdx }: CustomAddressFormProps) {
  const closeDrawer = useCustomAddressStore((s) => s.closeDrawer);
  const { data: detail, isLoading: isDetailLoading } = useCustomAddressDetail(caIdx);
  const upsertMutation = useUpsertCustomAddress();
  const deleteMutation = useDeleteCustomAddress();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (mode === "edit" && detail) reset(toFormValues(detail));
  }, [mode, detail, reset]);

  const latitude = useWatch({ control, name: "latitude" });
  const longitude = useWatch({ control, name: "longitude" });
  const addr03 = useWatch({ control, name: "addr03" });

  const onSubmit = (values: FormValues) => {
    const req: ReqUpsertCustomAddress = {
      CaIdx: mode === "edit" && caIdx != null ? caIdx : 0,
      Addr01: values.addr01,
      Addr02: values.addr02,
      Addr03: values.addr03,
      Addr04: values.addr04,
      RoadName: values.roadName,
      BuildingNo: values.buildingNo,
      PlaceName: values.placeName,
      Latitude: values.latitude,
      Longitude: values.longitude,
      Keywords: splitKeywords(values.keywordsText),
      Memo: values.memo,
    };
    upsertMutation.mutate(req);
  };

  const isBusy = isSubmitting || upsertMutation.isPending || deleteMutation.isPending;

  if (mode === "edit" && isDetailLoading) {
    return <div className="p-4 text-xs text-gray-400">불러오는 중...</div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
        <section className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-900">지번 주소 (법정동 체계)</h3>
          <div className="grid grid-cols-4 gap-2">
            <FormField label="시/도" required error={errors.addr01?.message}>
              <input {...register("addr01")} placeholder="경기" className={cnInput()} />
            </FormField>
            <FormField label="시/군/구" error={errors.addr02?.message}>
              <input {...register("addr02")} placeholder="평택시" className={cnInput()} />
            </FormField>
            <FormField label="읍/면/동" error={errors.addr03?.message}>
              <input {...register("addr03")} placeholder="포승읍 만호리" className={cnInput()} />
            </FormField>
            <FormField label="지번" error={errors.addr04?.message}>
              <input {...register("addr04")} placeholder="산12-3" className={cnInput()} />
            </FormField>
          </div>
          {!addr03?.trim() && (
            <p className="text-[11px] text-amber-600">
              읍/면/동이 비면 정보망(24시·인성 등) 접수 시 주소 단계 검증에 걸릴 수 있습니다.
            </p>
          )}
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-900">도로명 · 장소명 (선택)</h3>
          <div className="grid grid-cols-3 gap-2">
            <FormField label="도로명" error={errors.roadName?.message} className="col-span-1">
              <input {...register("roadName")} placeholder="포승공단로" className={cnInput()} />
            </FormField>
            <FormField label="건물번호" error={errors.buildingNo?.message}>
              <input {...register("buildingNo")} placeholder="118-3" className={cnInput()} />
            </FormField>
            <FormField
              label="장소명"
              error={errors.placeName?.message}
              hint="검색 결과에 함께 표시"
            >
              <input {...register("placeName")} placeholder="OO물류센터" className={cnInput()} />
            </FormField>
          </div>
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-900">좌표</h3>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="위도" required error={errors.latitude?.message}>
              <input
                {...register("latitude")}
                type="number"
                step="any"
                placeholder="36.9876543"
                className={cnInput("tabular-nums")}
              />
            </FormField>
            <FormField label="경도" required error={errors.longitude?.message}>
              <input
                {...register("longitude")}
                type="number"
                step="any"
                placeholder="126.8765432"
                className={cnInput("tabular-nums")}
              />
            </FormField>
          </div>
          <CoordinatePicker
            lat={Number(latitude)}
            lng={Number(longitude)}
            onChange={(lat, lng) => {
              setValue("latitude", lat, { shouldValidate: true, shouldDirty: true });
              setValue("longitude", lng, { shouldValidate: true, shouldDirty: true });
            }}
          />
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-900">검색 · 메모</h3>
          <FormField
            label="검색 키워드"
            error={errors.keywordsText?.message}
            hint="쉼표로 구분. 약칭·옛 지명 등 현장에서 부르는 말 (예: 포승공단 3블럭, 만호리 농장)"
          >
            <input {...register("keywordsText")} className={cnInput()} />
          </FormField>
          <FormField label="메모" error={errors.memo?.message}>
            <textarea
              {...register("memo")}
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs outline-none focus:border-blue-500"
            />
          </FormField>
        </section>
      </div>

      <div className="flex shrink-0 items-center justify-between border-t border-gray-200 px-4 py-3">
        <div>
          {mode === "edit" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs text-red-600 hover:text-red-700"
              disabled={isBusy}
              onClick={() => setDeleteOpen(true)}
            >
              삭제
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" className="text-xs" onClick={closeDrawer} disabled={isBusy}>
            취소
          </Button>
          <Button type="submit" size="sm" className="bg-blue-600 text-xs font-bold hover:bg-blue-700" disabled={isBusy}>
            {mode === "edit" ? "수정 저장" : "등록"}
          </Button>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>보완 주소를 삭제할까요?</AlertDialogTitle>
            <AlertDialogDescription>
              삭제 즉시 ULA 주소 검색에서 사라집니다. 이미 접수된 화물의 주소는 바뀌지 않습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (caIdx != null) deleteMutation.mutate({ CaIdx: caIdx });
              }}
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
